import React from 'react';
import { useGame } from '../context/GameContext';
import GameCard from '../components/GameCard';
import DeckSidebar from '../components/DeckSidebar';
import { relicById } from '../game/relics';
import { Coins, Trash2, RotateCw, Sparkles, TrendingUp } from 'lucide-react';

export default function Shop() {
  const { pendingShop, buyCard, buyRelic, leaveShop, useRemoveService, useShopUpgrade, useShopBlessing, run, floatingText, rerollShop } = useGame();
  if (!pendingShop) return null;
  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-8">
      <DeckSidebar deck={run?.player?.deck || []} />
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.4em] text-[color:var(--text-dim)]">DEALER</div>
          <h1 className="font-display text-5xl font-black uppercase text-[color:var(--pink)]">THE SHOP</h1>
        </div>
        <div className="flex items-center gap-3 brutal-border p-3">
          <Coins className="text-[color:var(--neon)]" />
          <span className="font-display text-2xl font-black" data-testid="shop-player-gold">{run.player.gold}</span>
        </div>
      </div>

      {floatingText && <div className="mt-4 text-[color:var(--red)] uppercase text-sm" data-testid="shop-floating-text">{floatingText}</div>}

      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">CARDS</div>
        <div className="flex flex-wrap gap-6">
          {pendingShop.cards.map((c, i) => c ? (
            <div key={i} className="flex flex-col items-center gap-2">
              <GameCard cardId={c.id} testid={`shop-card-${i}`} />
              <button
                className="brutal-btn text-sm px-4 py-2"
                onClick={() => buyCard(i)}
                disabled={run.player.gold < c.price}
                data-testid={`shop-card-buy-${i}`}
              >
                {c.price}G
              </button>
            </div>
          ) : (
            <div key={i} className="w-[168px] h-[240px] flex items-center justify-center border-2 border-dashed border-[color:var(--muted)] text-[color:var(--text-dim)] uppercase text-xs">
              SOLD
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">RELICS</div>
        <div className="flex flex-wrap gap-6">
          {pendingShop.relics.map((r, i) => {
            if (!r) return (
              <div key={i} className="w-[240px] h-[110px] flex items-center justify-center border-2 border-dashed border-[color:var(--muted)] text-[color:var(--text-dim)] uppercase text-xs">
                SOLD
              </div>
            );
            const rel = relicById(r.id);
            return (
              <div key={i} className="brutal-border-pink p-4 w-[240px] bg-[#0a0a0a]">
                <div className="text-4xl">{rel.art}</div>
                <div className="font-display font-black uppercase mt-2 text-[color:var(--pink)]">{rel.name}</div>
                <div className="text-xs mt-1 text-[color:var(--text-dim)]">{rel.desc}</div>
                <button className="brutal-btn-pink brutal-btn text-sm w-full mt-3"
                  onClick={() => buyRelic(i)}
                  disabled={run.player.gold < r.price}
                  data-testid={`shop-relic-buy-${i}`}>
                  {r.price}G
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-10">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-dim)] mb-3">SERVICES</div>
        <div className="brutal-border-dim p-4 w-fit flex items-center gap-4" style={{ borderColor: pendingShop.removeUsed ? 'var(--muted)' : 'var(--red)', boxShadow: pendingShop.removeUsed ? 'none' : '4px 4px 0 var(--red)' }}>
          <Trash2 className="text-[color:var(--red)]" />
          <div>
            <div className="font-display font-black uppercase text-[color:var(--red)]">DELETE A CARD</div>
            <div className="text-xs text-[color:var(--text-dim)]">Yeet a card out of your deck forever.</div>
          </div>
          <button
            className="brutal-btn-red brutal-btn text-sm"
            onClick={useRemoveService}
            disabled={pendingShop.removeUsed || run.player.gold < pendingShop.removePrice}
            data-testid="shop-remove-service-button"
          >
            {pendingShop.removeUsed ? 'DONE' : `${pendingShop.removePrice}G`}
          </button>
        </div>

        {/* v2.3 QoL: Shop reroll — cost scales with each use */}
        {(() => {
          const rerollCost = (pendingShop.rerollBasePrice || 40) + (pendingShop.rerollsUsed || 0) * 25;
          const canAfford = run.player.gold >= rerollCost;
          return (
            <div className="brutal-border-dim p-4 w-fit flex items-center gap-4 mt-3" style={{ borderColor: canAfford ? 'var(--pink)' : '#333', boxShadow: canAfford ? '4px 4px 0 var(--pink)' : 'none' }}>
              <RotateCw className="text-[color:var(--pink)]" />
              <div>
                <div className="font-display font-black uppercase text-[color:var(--pink)]">REROLL WARES</div>
                <div className="text-xs text-[color:var(--text-dim)]">Reshuffle all cards + relic on the shelf. Cost goes up each time.</div>
              </div>
              <button
                className="brutal-btn text-sm"
                onClick={rerollShop}
                disabled={!canAfford}
                data-testid="shop-reroll-button"
                style={{ borderColor: 'var(--pink)', color: 'var(--pink)' }}
              >
                {rerollCost}G {pendingShop.rerollsUsed > 0 && <span className="ml-1 opacity-70">(x{pendingShop.rerollsUsed})</span>}
              </button>
            </div>
          );
        })()}

        {/* v3.10 SHOP DEPTH: UPGRADE A CARD (one-time) */}
        <div
          className="brutal-border-dim p-4 w-fit flex items-center gap-4 mt-3"
          style={{
            borderColor: pendingShop.upgradeUsed ? 'var(--muted)' : '#ffd700',
            boxShadow:  pendingShop.upgradeUsed ? 'none' : '4px 4px 0 #ffd700',
          }}
        >
          <TrendingUp style={{ color: '#ffd700' }} />
          <div>
            <div className="font-display font-black uppercase" style={{ color: '#ffd700' }}>UPGRADE A CARD</div>
            <div className="text-xs text-[color:var(--text-dim)]">Level up one card in your deck. Choose the target on the next screen.</div>
          </div>
          <button
            className="brutal-btn text-sm"
            style={{ borderColor: '#ffd700', color: '#ffd700', boxShadow: pendingShop.upgradeUsed ? 'none' : '3px 3px 0 #ffd700' }}
            onClick={useShopUpgrade}
            disabled={pendingShop.upgradeUsed || run.player.gold < pendingShop.upgradePrice}
            data-testid="shop-upgrade-service-button"
          >
            {pendingShop.upgradeUsed ? 'DONE' : `${pendingShop.upgradePrice}G`}
          </button>
        </div>

        {/* v3.10 SHOP DEPTH: RANDOM BLESSING (one-time, common/rare only) */}
        <div
          className="brutal-border-dim p-4 w-fit flex items-center gap-4 mt-3"
          style={{
            borderColor: pendingShop.blessingUsed ? 'var(--muted)' : '#c084fc',
            boxShadow:  pendingShop.blessingUsed ? 'none' : '4px 4px 0 #c084fc',
          }}
        >
          <Sparkles style={{ color: '#c084fc' }} />
          <div>
            <div className="font-display font-black uppercase" style={{ color: '#c084fc' }}>BUY A BLESSING</div>
            <div className="text-xs text-[color:var(--text-dim)]">Pay to receive a random <b>non-legendary</b> boss blessing. Fate rolls once.</div>
          </div>
          <button
            className="brutal-btn text-sm"
            style={{ borderColor: '#c084fc', color: '#c084fc', boxShadow: pendingShop.blessingUsed ? 'none' : '3px 3px 0 #c084fc' }}
            onClick={useShopBlessing}
            disabled={pendingShop.blessingUsed || run.player.gold < pendingShop.blessingPrice}
            data-testid="shop-blessing-service-button"
          >
            {pendingShop.blessingUsed ? 'BLESSED' : `${pendingShop.blessingPrice}G`}
          </button>
        </div>
      </div>

      <div className="mt-auto pt-8 flex justify-end">
        <button className="brutal-btn" onClick={leaveShop} data-testid="leave-shop-button">LEAVE SHOP →</button>
      </div>
    </div>
  );
}
